import { proApiSlice } from '../../store/apiSlices/proApiSlice';
import { coreApiSlice } from '../../store/apiSlices/coreApiSlice';
import type { StripeInterval } from '../../utils/payment-utils';

export interface RegistrationRequest {
  first_name: string;
  last_name: string;
  email: string;
}

export interface SubscriptionPrice {
  id: string;
  cost: number;
  interval: StripeInterval;
}

export interface SubscriptionPrices {
  Plus: Array<SubscriptionPrice>;
  Pro: Array<SubscriptionPrice>;
  trial_length?: number;
  top_user_price?: Array<SubscriptionPrice>;
}

export interface NewSubscriptionCheckoutRequest {
  subscription_start_date?: string | null;
  customer_email: string | null;
  price_id: string;
  success_url: string;
  cancel_url: string;
  request_trial?: boolean;
  is_competition_winner?: boolean;
}

export interface NewPostTrialDiscountSubscriptionCheckoutRequest {
  user_id: string;
  interval: StripeInterval;
  success_url: string;
  refresh_url: string;
  cancel_url: string;
}

export interface InviteCodeSignUpRequest {
  email: string;
  invite_code: string;
}

export interface VerifyRegistrationRequest {
  checkout_session_id: string;
  user_id: string;
}

export interface VerifyTrialUpgradeRegistrationRequest {
  user_id: string;
}

export interface CheckoutSessionUserDetails {
  user_id: string | null;
  email: string;
  name: string;
}

export interface BrandDetails {
  id: string;
  brand_name: string;
  logo_image: string | null;
  subheading: string | null;
  description: string | null;
  location: object | null;
  business_type: string | null;
  email: string | null;
  phone_number: string | null;
  social_links: object | null;
  website_link: string | null;
  primary_image: string | null;
  gallery_images: string[] | null;
  discount_code: string | null;
  offer_link: string | null;
  priority: number | null;
  is_active: boolean;
  tier: string;
}

// Message from Brevo API to filter for
export const alreadyExistsMessage = 'Already on the list';

export const extendedApiSlice = proApiSlice
  .enhanceEndpoints({ addTagTypes: ['Subscription'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      registration: builder.mutation<void, RegistrationRequest>({
        query: (request) => ({
          url: 'register/waitlist',
          method: 'POST',
          data: request,
          useDefaultErrorHandler: false,
        }),
      }),
      getPrices: builder.query<SubscriptionPrices, void>({
        query: () => ({
          url: 'register/prices',
          method: 'GET',
        }),
      }),
      partnerInviteCodeSignUp: builder.mutation<
        string | undefined,
        InviteCodeSignUpRequest
      >({
        query: (request) => ({
          url: 'register/invite-code-sign-up',
          method: 'POST',
          data: request,
        }),
        transformResponse: (response: { redirect_url: string } | undefined) =>
          response?.redirect_url,
      }),
      createNewSubscriptionCheckout: builder.mutation<
        string | undefined,
        NewSubscriptionCheckoutRequest
      >({
        query: (request) => ({
          url: 'register/new-subscription-checkout-session',
          method: 'POST',
          data: request,
        }),
        transformResponse: (response: { redirect_url: string } | undefined) =>
          response?.redirect_url,
      }),
      createNewPostTrialDiscountCheckout: builder.mutation<
        string | undefined,
        NewPostTrialDiscountSubscriptionCheckoutRequest
      >({
        query: (request) => ({
          url: 'register/post-trial-subscription-checkout-session',
          method: 'POST',
          data: request,
          useDefaultErrorHandler: false,
        }),
        transformResponse: (response: { redirect_url: string } | undefined) =>
          response?.redirect_url,
      }),
      confirmRegistration: builder.mutation<void, VerifyRegistrationRequest>({
        query: (request) => ({
          url: 'register/confirm_web_subscription',
          data: request,
          method: 'PUT',
          useDefaultErrorHandler: false,
        }),
        invalidatesTags: ['Subscription'],
      }),
      confirmTrialUpgradeRegistration: builder.mutation<
        void,
        VerifyTrialUpgradeRegistrationRequest
      >({
        query: (request) => ({
          url: 'register/confirm_web_post_trial_upgrade',
          data: request,
          method: 'PUT',
          useDefaultErrorHandler: false,
        }),
        invalidatesTags: ['Subscription'],
      }),
      getCheckoutCustomerDetails: builder.query<
        CheckoutSessionUserDetails,
        string
      >({
        query: (checkout_session_id) => ({
          url: 'register/web_checkout_customer',
          params: { checkout_session_id },
          method: 'GET',
          useDefaultErrorHandler: false,
        }),
        transformResponse: (response: {
          status: string;
          result: CheckoutSessionUserDetails;
        }) => response?.result ?? response,
      }),
      triggerWebhooks: builder.mutation<void, void>({
        query: () => ({
          url: 'webhooks/trigger_pending',
          method: 'POST',
          useDefaultErrorHandler: false,
        }),
      }),
    }),
  });

export const extendedCoreApiSlice = coreApiSlice
  .enhanceEndpoints({ addTagTypes: [] })
  .injectEndpoints({
    endpoints: (builder) => ({
      verifyBrandInviteCodeDetails: builder.query<BrandDetails, string | null>({
        query: (inviteCode) => ({
          url: 'marketing/brands/invite-code/verify-and-brand-details',
          params: { code: inviteCode },
          method: 'GET',
          useDefaultErrorHandler: false,
        }),
      }),
    }),
  });

export const {
  useRegistrationMutation,
  useGetPricesQuery,
  useCreateNewSubscriptionCheckoutMutation,
  useCreateNewPostTrialDiscountCheckoutMutation,
  usePartnerInviteCodeSignUpMutation,
  useConfirmRegistrationMutation,
  useConfirmTrialUpgradeRegistrationMutation,
  useLazyGetCheckoutCustomerDetailsQuery,
  useGetCheckoutCustomerDetailsQuery,
  useTriggerWebhooksMutation,
} = extendedApiSlice;

export const { useVerifyBrandInviteCodeDetailsQuery } = extendedCoreApiSlice;
